import React, { useState, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col, Modal, ModalHeader, ModalBody } from "reactstrap";
import SimpleBar from "simplebar-react";
import { withTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { loadAuth } from "../../../controllers/auth/auth";
import { getNotifications, markAsRead, markAllAsRead, deleteNotification, deleteAllRead } from "../../../controllers/notifications/notifications";

dayjs.extend(relativeTime);

const TYPE_META = {
  info:       { icon: 'mdi-bell-outline',           bg: '#3b82f6' },
  leave:      { icon: 'mdi-calendar-clock',          bg: '#8b5cf6' },
  permission: { icon: 'mdi-calendar-check',          bg: '#10b981' },
  task:       { icon: 'mdi-checkbox-marked-outline', bg: '#f59e0b' },
  warning:    { icon: 'mdi-alert',                   bg: '#f97316' },
  error:      { icon: 'mdi-alert-circle',            bg: '#ef4444' },
};

const ActionBtn = ({ icon, title, color = '#94a3b8', hoverColor = '#475569', onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '26px', height: '26px', borderRadius: '6px', border: 'none',
        background: hovered ? '#f1f5f9' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0,
        color: hovered ? hoverColor : color,
        padding: 0,
      }}
    >
      <i className={`mdi ${icon}`} style={{ fontSize: '15px' }} />
    </button>
  );
};

const NotificationDropdown = ({ t }) => {
  const [menu, setMenu] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [modalNotif, setModalNotif] = useState(null);
  const esRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getNotifications().then(data => {
      setItems(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let es;

    const connect = async () => {
      try {
        const [, token] = await loadAuth();
        if (cancelled) return;
        const url = `${process.env.REACT_APP_API_URL}notifications-stream?token=${encodeURIComponent(token)}&appmode=core`;
        es = new EventSource(url);
        esRef.current = es;

        es.addEventListener('init', e => {
          if (!cancelled) setUnreadCount(JSON.parse(e.data).unreadCount);
        });

        es.addEventListener('notification', e => {
          if (cancelled) return;
          const notif = JSON.parse(e.data);
          setItems(prev => [notif, ...prev]);
          setUnreadCount(prev => prev + 1);
          enqueueSnackbar(notif.n_title, {
            variant: 'info',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        });

        es.addEventListener('update', e => {
          if (cancelled) return;
          const payload = JSON.parse(e.data);
          setUnreadCount(payload.unreadCount ?? 0);
          if (payload.action === 'read' && payload.n_id) {
            setItems(prev => prev.map(n => n.n_id === payload.n_id ? { ...n, n_is_read: true } : n));
          } else if (payload.action === 'readAll') {
            setItems(prev => prev.map(n => ({ ...n, n_is_read: true })));
          } else if (payload.action === 'delete' && payload.n_id) {
            setItems(prev => prev.filter(n => n.n_id !== payload.n_id));
            setModalNotif(prev => prev?.n_id === payload.n_id ? null : prev);
          } else if (payload.action === 'clearRead') {
            setItems(prev => prev.filter(n => !n.n_is_read));
            setModalNotif(prev => (prev && prev.n_is_read) ? null : prev);
          }
        });

        es.onerror = () => {};
      } catch (err) {
        console.error('Notification SSE error:', err);
      }
    };

    connect();
    return () => {
      cancelled = true;
      if (es) es.close();
    };
  }, []);

  const toggleExpand = (e, n_id) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(n_id) ? next.delete(n_id) : next.add(n_id);
      return next;
    });
  };

  const handleMarkAsRead = (e, n_id) => {
    e.stopPropagation();
    setItems(prev => prev.map(n => n.n_id === n_id ? { ...n, n_is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    markAsRead({ n_id });
  };

  const handleDelete = (e, n_id, is_read) => {
    e.stopPropagation();
    setItems(prev => prev.filter(n => n.n_id !== n_id));
    if (!is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    setExpandedIds(prev => { const next = new Set(prev); next.delete(n_id); return next; });
    setModalNotif(prev => prev?.n_id === n_id ? null : prev);
    deleteNotification({ n_id });
  };

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    setItems(prev => prev.map(n => ({ ...n, n_is_read: true })));
    setUnreadCount(0);
    markAllAsRead();
  };

  const handleDeleteAllRead = (e) => {
    e.stopPropagation();
    setItems(prev => prev.filter(n => !n.n_is_read));
    setModalNotif(prev => (prev && prev.n_is_read) ? null : prev);
    deleteAllRead();
  };

  const handleOpenModal = (e, notif) => {
    e.stopPropagation();
    setModalNotif(notif);
  };

  const readCount = items.filter(n => n.n_is_read).length;
  const modalMeta = modalNotif ? (TYPE_META[modalNotif.n_type] || TYPE_META.info) : null;

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon waves-effect"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className="mdi mdi-bell-outline" />
          {unreadCount > 0 && (
            <span className="badge bg-danger rounded-pill">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0">
          {/* Header */}
          <div className="p-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0 font-size-16">
                  {t("Notifications")}
                  {unreadCount > 0 && (
                    <span style={{ marginLeft: '6px', fontSize: '11px', color: '#3b82f6', fontWeight: 600 }}>
                      ({unreadCount} unread)
                    </span>
                  )}
                </h6>
              </Col>
              <Col xs="auto" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    style={{ border: 'none', background: 'none', color: '#3b82f6', fontSize: '11px', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                  >
                    {t("Mark all read")}
                  </button>
                )}
                {readCount > 0 && (
                  <button
                    onClick={handleDeleteAllRead}
                    style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: '11px', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                  >
                    {t("Clear read")}
                  </button>
                )}
              </Col>
            </Row>
          </div>

          <SimpleBar style={{ height: '340px' }}>
            {loading && (
              <div className="text-center p-4" style={{ color: '#94a3b8' }}>
                <i className="mdi mdi-loading mdi-spin" style={{ fontSize: '22px' }} />
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="text-center p-4" style={{ color: '#94a3b8' }}>
                <i className="mdi mdi-bell-off-outline" style={{ fontSize: '30px', display: 'block', marginBottom: '6px' }} />
                <span style={{ fontSize: '13px' }}>{t("No notifications")}</span>
              </div>
            )}

            {items.map(notif => {
              const meta = TYPE_META[notif.n_type] || TYPE_META.info;
              const isExpanded = expandedIds.has(notif.n_id);
              const hasBody = notif.n_body && notif.n_body.trim().length > 0;

              return (
                <div
                  key={notif.n_id}
                  onClick={e => toggleExpand(e, notif.n_id)}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #f1f5f9',
                    background: notif.n_is_read ? '#fff' : '#eff6ff',
                    transition: 'background 0.15s',
                    cursor: hasBody ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    {/* type icon */}
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                      background: `${meta.bg}1a`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className={`mdi ${meta.icon}`} style={{ color: meta.bg, fontSize: '16px' }} />
                    </div>

                    {/* content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* title + unread dot */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {!notif.n_is_read && (
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                        )}
                        <div style={{
                          fontSize: '13px', fontWeight: notif.n_is_read ? 500 : 700,
                          color: '#1e293b', lineHeight: 1.4, flex: 1, minWidth: 0,
                        }}>
                          {notif.n_title}
                        </div>
                      </div>

                      {/* body */}
                      {hasBody && (
                        <div style={{
                          fontSize: '11.5px', color: '#64748b',
                          marginTop: '4px', lineHeight: 1.55,
                          overflow: 'hidden',
                          maxHeight: isExpanded ? '600px' : '1.55em',
                          transition: 'max-height 0.3s ease',
                          whiteSpace: isExpanded ? 'pre-line' : 'nowrap',
                          textOverflow: isExpanded ? 'unset' : 'ellipsis',
                        }}>
                          {notif.n_body}
                        </div>
                      )}

                      {/* magnify button — only visible when expanded */}
                      {hasBody && isExpanded && (
                        <button
                          onClick={e => handleOpenModal(e, notif)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            marginTop: '6px', padding: '2px 8px', border: '1px solid #e2e8f0',
                            borderRadius: '4px', background: '#f8fafc', cursor: 'pointer',
                            fontSize: '10.5px', color: '#3b82f6', fontWeight: 600,
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                          onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                        >
                          <i className="mdi mdi-magnify" style={{ fontSize: '13px' }} />
                          {t("View full")}
                        </button>
                      )}

                      {/* timestamp + chevron */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                        <span style={{ fontSize: '10.5px', color: '#94a3b8' }}>
                          {dayjs(notif.n_created_at).fromNow()}
                        </span>
                        {hasBody && (
                          <i
                            className={`mdi ${isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'}`}
                            style={{ fontSize: '15px', color: '#94a3b8' }}
                          />
                        )}
                      </div>
                    </div>

                    {/* action buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0 }}>
                      {!notif.n_is_read && (
                        <ActionBtn
                          icon="mdi-check"
                          title={t("Mark as read")}
                          color="#94a3b8"
                          hoverColor="#10b981"
                          onClick={e => handleMarkAsRead(e, notif.n_id)}
                        />
                      )}
                      <ActionBtn
                        icon="mdi-trash-can-outline"
                        title={t("Delete")}
                        color="#94a3b8"
                        hoverColor="#ef4444"
                        onClick={e => handleDelete(e, notif.n_id, notif.n_is_read)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </SimpleBar>

          {/* Footer */}
          <div className="p-2 border-top d-grid">
            <span
              className="btn btn-sm btn-link font-size-14 btn-block text-center"
              style={{ color: '#3b82f6', cursor: 'default' }}
            >
              <i className="mdi mdi-bell-outline me-1" />
              {items.length > 0 ? `${items.length} ${t("notifications")}` : t("No notifications")}
            </span>
          </div>
        </DropdownMenu>
      </Dropdown>

      {/* Full-message modal */}
      {modalNotif && (
        <Modal isOpen={!!modalNotif} toggle={() => setModalNotif(null)} centered size="md">
          <ModalHeader
            toggle={() => setModalNotif(null)}
            style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                background: `${modalMeta.bg}1a`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className={`mdi ${modalMeta.icon}`} style={{ color: modalMeta.bg, fontSize: '16px' }} />
              </div>
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
                {modalNotif.n_title}
              </span>
            </div>
          </ModalHeader>
          <ModalBody style={{ padding: '20px 24px' }}>
            {modalNotif.n_body ? (
              <p style={{
                fontSize: '13px', color: '#334155', lineHeight: 1.7,
                whiteSpace: 'pre-line', margin: 0,
              }}>
                {modalNotif.n_body}
              </p>
            ) : (
              <p style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>
                {t("No message body")}
              </p>
            )}
            <div style={{
              marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                {dayjs(modalNotif.n_created_at).format('DD MMM YYYY, HH:mm')}
                {' · '}
                {dayjs(modalNotif.n_created_at).fromNow()}
              </span>
              <span style={{
                fontSize: '10.5px', fontWeight: 600, padding: '2px 8px',
                borderRadius: '99px', background: `${modalMeta.bg}1a`, color: modalMeta.bg,
              }}>
                {modalNotif.n_type}
              </span>
            </div>
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
};

NotificationDropdown.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(NotificationDropdown);
